import {
  Building2,
  Facebook,
  Image as ImageIcon,
  Instagram,
  Loader2,
  RefreshCw,
  Share2,
  User,
  Video as VideoIcon,
} from "lucide-react";
import { SiGoogle } from "react-icons/si";
import React, { useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { showError } from "../../shared/alertService";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Modal from "../ui/Modal";

interface UnifiedRepublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRepublishLinkedIn: (usePersonalNetwork: boolean) => Promise<void>;
  onPublishMultiPlatform: (platforms: string[]) => Promise<void>;
  post: any;
  loading?: boolean;
}

const LINKEDIN_OPTIONS = [
  {
    id: "linkedin_personal",
    name: "Rede Pessoal",
    description: "Publicar no seu perfil pessoal do LinkedIn",
    icon: <User className="h-5 w-5" />,
  },
  {
    id: "linkedin_organization",
    name: "Organização",
    description:
      "Publicar na página da empresa (usa credenciais da organização selecionada no menu)",
    icon: <Building2 className="h-5 w-5" />,
  },
];

const OTHER_PLATFORMS = [
  {
    id: "google_my_business",
    name: "Google Business",
    description: "Google Business Profile (Minha Empresa)",
    icon: <SiGoogle className="h-5 w-5" />,
    requiresVideo: false,
  },
  {
    id: "instagram_feed",
    name: "Instagram Feed",
    description: "Publicação normal no feed do Instagram",
    icon: <Instagram className="h-5 w-5" />,
    requiresVideo: false,
  },
  {
    id: "instagram_reels",
    name: "Instagram Reels",
    description: "Vídeos curtos no Instagram",
    icon: <VideoIcon className="h-5 w-5" />,
    requiresVideo: true,
  },
  {
    id: "facebook_feed",
    name: "Facebook Feed",
    description: "Publicação normal no feed do Facebook",
    icon: <Facebook className="h-5 w-5" />,
    requiresVideo: false,
  },
  {
    id: "facebook_reels",
    name: "Facebook Reels",
    description: "Vídeos curtos no Facebook",
    icon: <VideoIcon className="h-5 w-5" />,
    requiresVideo: true,
  },
  {
    id: "facebook_page",
    name: "Facebook Page",
    description: "Página do Facebook",
    icon: <Facebook className="h-5 w-5" />,
    requiresVideo: false,
  },
  {
    id: "instagram_stories",
    name: "Instagram Stories",
    description: "Publicação automática ou assistida nos Stories",
    icon: <Instagram className="h-5 w-5" />,
    requiresVideo: false,
  },
  {
    id: "facebook_stories",
    name: "Facebook Stories",
    description: "Stories na página do Facebook",
    icon: <Facebook className="h-5 w-5" />,
    requiresVideo: false,
  },
  {
    id: "youtube",
    name: "YouTube",
    description: "Upload de vídeo no canal do YouTube",
    icon: <VideoIcon className="h-5 w-5 text-red-600" />,
    requiresVideo: true,
  },
];

const UnifiedRepublishModal: React.FC<UnifiedRepublishModalProps> = ({
  isOpen,
  onClose,
  onRepublishLinkedIn,
  onPublishMultiPlatform,
  post,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [republishing, setRepublishing] = useState(false);

  const handleOptionToggle = (optionId: string) => {
    setSelectedOptions((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  };

  const handleRepublish = async () => {
    if (selectedOptions.length === 0) return;

    setRepublishing(true);
    try {
      const linkedinOptions = selectedOptions.filter((opt) =>
        opt.startsWith("linkedin_"),
      );
      const otherPlatforms = selectedOptions.filter(
        (opt) => !opt.startsWith("linkedin_"),
      );

      if (linkedinOptions.length > 0) {
        for (const option of linkedinOptions) {
          const usePersonalNetwork = option === "linkedin_personal";
          await onRepublishLinkedIn(usePersonalNetwork);
        }
      }
      if (otherPlatforms.length > 0) {
        await onPublishMultiPlatform(otherPlatforms);
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
      onClose();
    } catch (error) {
      console.error("Erro ao republicar:", error);
      showError(
        t("Erro ao republicar: {message}", {
          message:
            error instanceof Error ? error.message : t("Erro desconhecido"),
        }),
        t("Erro ao republicar"),
      );
    } finally {
      setRepublishing(false);
    }
  };

  const hasVideo = post?.video_info?.filename;
  const hasImage = post?.image_info?.filename;
  const isLoading = republishing || loading;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={t("Republicar Post")}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex flex-row justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="button"
            variant="primary"
            icon={
              isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )
            }
            onClick={handleRepublish}
            disabled={selectedOptions.length === 0 || isLoading}
          >
            {isLoading
              ? t("Processando...")
              : selectedOptions.length === 1
                ? t("Republicar em {count} destino", {
                    count: selectedOptions.length,
                  })
                : t("Republicar em {count} destinos", {
                    count: selectedOptions.length,
                  })}
          </Button>
        </div>
      }
    >
      <div className="py-2">
        <div className="flex items-start gap-4 mb-6">
          <div
            className={cn(
              "shrink-0 w-12 h-12 rounded-full flex items-center justify-center",
              "bg-purple-100 text-purple-600",
            )}
          >
            <RefreshCw className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {t("Onde republicar")}
            </h3>
            <p className="text-sm text-gray-600">
              {t("O post")}{" "}
              <span className="font-semibold text-gray-900">#{post?.id}</span>{" "}
              {t(
                "será republicado nos destinos selecionados. As credenciais do LinkedIn vêm da",
              )}{" "}
              <span className="font-medium text-gray-700">
                {t("organização selecionada")}
              </span>{" "}
              {t("no menu de contexto (canto superior).")}
            </p>
          </div>
        </div>

        <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-300">
          <h3 className="font-medium text-gray-900 mb-2">
            {t("Post #{id}", { id: post?.id })}
          </h3>
          <p className="text-sm text-gray-700 mb-2 whitespace-pre-wrap wrap-break-word line-clamp-3">
            {post?.content}
          </p>
          <div className="flex gap-2 text-xs flex-wrap">
            {hasImage && (
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-sm inline-flex items-center gap-1">
                <ImageIcon className="h-3 w-3" /> {t("Imagem")}
              </span>
            )}
            {hasVideo && (
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-sm inline-flex items-center gap-1">
                <VideoIcon className="h-3 w-3" /> {t("Vídeo")}
              </span>
            )}
          </div>
        </div>

        {hasImage && hasVideo && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="text-sm text-yellow-800">
              <p className="font-medium">{t("Atenção:")}</p>
              <p>
                {t(
                  "Este post tem imagem e vídeo. O LinkedIn publicará apenas o vídeo (ignora imagem).",
                )}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3 mb-4">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-600" />
            {t("Republicar no LinkedIn")}
          </h3>
          {LINKEDIN_OPTIONS.map((option) => {
            const isSelected = selectedOptions.includes(option.id);
            const isPersonal = option.id === "linkedin_personal";
            return (
              <div
                key={option.id}
                className={cn(
                  "p-4 border rounded-lg cursor-pointer transition-all",
                  isSelected && isPersonal && "border-blue-500 bg-blue-50",
                  isSelected && !isPersonal && "border-orange-500 bg-orange-50",
                  !isSelected && "border-purple-300 hover:border-purple-400",
                )}
                onClick={() => handleOptionToggle(option.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="shrink-0 text-gray-600">
                      {option.icon}
                    </span>
                    <div className="min-w-0">
                      <h4 className="font-medium text-gray-900">
                        {t(option.name)}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {t(option.description)}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div
                      className={cn(
                        "w-5 h-5 rounded-full shrink-0",
                        isPersonal ? "bg-blue-600" : "bg-orange-600",
                      )}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-3">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-purple-600" />
            {t("Publicar em Outras Redes")}
          </h3>
          {OTHER_PLATFORMS.map((platform) => {
            const isSelected = selectedOptions.includes(platform.id);
            const canSelect = !platform.requiresVideo || hasVideo;
            const isDisabled = !canSelect || republishing;
            return (
              <div
                key={platform.id}
                className={cn(
                  "p-4 border rounded-lg transition-all",
                  isDisabled && "opacity-50 cursor-not-allowed bg-gray-100",
                  !isDisabled && "cursor-pointer hover:border-purple-300",
                  isSelected && !isDisabled && "border-purple-500 bg-purple-50",
                  !isSelected && "border-purple-300",
                )}
                onClick={() => !isDisabled && handleOptionToggle(platform.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="shrink-0 text-purple-600">
                      {platform.icon}
                    </span>
                    <div className="min-w-0">
                      <h4 className="font-medium text-gray-900">
                        {platform.name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {t(platform.description)}
                      </p>
                      {platform.requiresVideo && !hasVideo && (
                        <p className="text-xs text-red-600 mt-1">
                          ⚠️ {t("Requer vídeo")}
                        </p>
                      )}
                    </div>
                  </div>
                  {isSelected && !isDisabled && (
                    <div className="w-5 h-5 rounded-full bg-purple-600 shrink-0" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default UnifiedRepublishModal;
